import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, FileText, Calendar, Settings, LogOut, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [usuario, setUsuario] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('resultados');
  const [editData, setEditData] = useState({
    nombre: '',
    telefono: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Debug: Log editMode changes
  useEffect(() => {
    console.log('editMode changed to:', editMode);
  }, [editMode]);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Load user data first
      const userResponse = await api.get('/auth/me');
      setUsuario(userResponse.data);
      setEditData({
        nombre: userResponse.data.nombre || '',
        telefono: userResponse.data.telefono || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      // Load resultados separately to handle errors gracefully
      try {
        const resultadosResponse = await api.get('/resultados');
        setResultados(resultadosResponse.data);
      } catch (resultadosError) {
        console.error('Error loading resultados:', resultadosError);
        setResultados([]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la información del usuario',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('userAdmin');
    navigate('/');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    console.log('handleUpdateProfile called, editMode:', editMode);
    
    if (!editMode) {
      console.log('Not in edit mode, ignoring submit');
      return;
    }
    
    // Validar contraseñas si se están cambiando
    if (editData.new_password) {
      if (!editData.current_password) {
        toast({
          title: 'Error',
          description: 'Debes ingresar tu contraseña actual',
          variant: 'destructive',
        });
        return;
      }
      if (editData.new_password !== editData.confirm_password) {
        toast({
          title: 'Error',
          description: 'Las contraseñas nuevas no coinciden',
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      const updatePayload = {
        nombre: editData.nombre,
        telefono: editData.telefono
      };

      // Incluir cambio de contraseña si se proporcionó
      if (editData.new_password) {
        updatePayload.current_password = editData.current_password;
        updatePayload.new_password = editData.new_password;
      }

      await api.put('/auth/me', updatePayload);
      
      toast({
        title: '✅ Perfil actualizado',
        description: 'Tu información ha sido actualizada exitosamente',
      });
      
      setEditMode(false);
      loadUserData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo actualizar el perfil',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e40af]" />
      </div>
    );
  }

  if (!usuario) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Mi Cuenta</h1>
              <p className="text-lg opacity-90">Bienvenido, {usuario?.nombre}</p>
            </div>
            <Button 
              variant="outline" 
              className="text-white border-white hover:bg-white hover:text-[#1e40af]"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="resultados" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Resultados
            </TabsTrigger>
            <TabsTrigger value="perfil" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Mi Perfil
            </TabsTrigger>
            <TabsTrigger value="mascotas" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Mis Mascotas
            </TabsTrigger>
          </TabsList>

          {/* Resultados Tab */}
          <TabsContent value="resultados" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Mis Resultados</CardTitle>
              </CardHeader>
              <CardContent>
                {resultados.length > 0 ? (
                  <div className="space-y-4">
                    {resultados.map((resultado) => (
                      <div 
                        key={resultado.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="bg-[#84cc16] p-3 rounded-lg">
                            <FileText className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800">{resultado.estudio_nombre}</h4>
                            <p className="text-sm text-gray-600">Paciente: {resultado.paciente_nombre}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(resultado.created_at).toLocaleDateString('es-MX')}
                            </p>
                            {resultado.notas && (
                              <p className="text-xs text-gray-600 mt-1">
                                <span className="font-semibold">Notas:</span> {resultado.notas}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button 
                          className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                          onClick={() => window.open(resultado.pdf_url, '_blank')}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Descargar
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p>No tienes resultados disponibles aún.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Perfil Tab */}
          <TabsContent value="perfil" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">
                  Información Personal
                  <span className="text-xs ml-2 text-gray-500">
                    (EditMode: {editMode ? 'ON' : 'OFF'})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="flex items-center gap-6">
                    <div className="h-24 w-24 bg-[#1e40af] rounded-full flex items-center justify-center text-white text-3xl font-bold">
                      {usuario.nombre.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">{usuario.nombre}</h3>
                      <p className="text-gray-600">{usuario.email}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre completo
                      </label>
                      <Input
                        type="text"
                        value={editMode ? editData.nombre : usuario.nombre}
                        onChange={(e) => setEditData({...editData, nombre: e.target.value})}
                        disabled={!editMode}
                        className={!editMode ? "bg-gray-50" : ""}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correo electrónico
                      </label>
                      <Input
                        type="email"
                        value={usuario.email}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">El email no se puede cambiar</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono
                      </label>
                      <Input
                        type="tel"
                        value={editMode ? editData.telefono : usuario.telefono}
                        onChange={(e) => setEditData({...editData, telefono: e.target.value})}
                        disabled={!editMode}
                        className={!editMode ? "bg-gray-50" : ""}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cédula Profesional
                      </label>
                      <Input
                        type="text"
                        value={usuario.cedula_profesional}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">La cédula no se puede cambiar</p>
                    </div>
                  </div>

                  {editMode && (
                    <div className="border-t pt-6">
                      <h4 className="font-semibold text-gray-800 mb-4">Cambiar Contraseña (Opcional)</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contraseña actual
                          </label>
                          <Input
                            type="password"
                            value={editData.current_password}
                            onChange={(e) => setEditData({...editData, current_password: e.target.value})}
                            placeholder="Dejar vacío si no cambias"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nueva contraseña
                          </label>
                          <Input
                            type="password"
                            value={editData.new_password}
                            onChange={(e) => setEditData({...editData, new_password: e.target.value})}
                            placeholder="Dejar vacío si no cambias"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirmar contraseña
                          </label>
                          <Input
                            type="password"
                            value={editData.confirm_password}
                            onChange={(e) => setEditData({...editData, confirm_password: e.target.value})}
                            placeholder="Dejar vacío si no cambias"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 flex gap-3">
                    {!editMode ? (
                      <Button 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Clicking Editar perfil, current editMode:', editMode);
                          setEditMode(true);
                          console.log('After setEditMode(true)');
                        }}
                        className="bg-[#84cc16] hover:bg-[#65a30d] text-white"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Editar perfil
                      </Button>
                    ) : (
                      <>
                        <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                          Guardar cambios
                        </Button>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setEditMode(false);
                            setEditData({
                              nombre: usuario.nombre,
                              telefono: usuario.telefono,
                              current_password: '',
                              new_password: '',
                              confirm_password: ''
                            });
                          }}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mascotas Tab */}
          <TabsContent value="mascotas" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[#1e3a8a]">Mis Mascotas</CardTitle>
                  <Button className="bg-[#84cc16] hover:bg-[#65a30d] text-white">
                    Agregar mascota
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {usuario.mascotas && usuario.mascotas.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {usuario.mascotas.map((mascota) => (
                      <div 
                        key={mascota.id}
                        className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="text-xl font-bold text-[#1e3a8a]">{mascota.nombre}</h4>
                            <p className="text-gray-600">{mascota.especie} - {mascota.raza}</p>
                          </div>
                          <span className="bg-[#84cc16] text-white px-3 py-1 rounded-full text-sm font-semibold">
                            {mascota.edad} años
                          </span>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600">
                          <p><span className="font-semibold">Especie:</span> {mascota.especie}</p>
                          <p><span className="font-semibold">Raza:</span> {mascota.raza}</p>
                          <p><span className="font-semibold">Edad:</span> {mascota.edad} años</p>
                        </div>
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <Button variant="outline" className="w-full text-[#1e40af] border-[#1e40af] hover:bg-[#1e40af] hover:text-white">
                            Ver historial
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p>No tienes mascotas registradas aún.</p>
                    <p className="text-sm mt-2">Agrega una mascota para gestionar su historial médico.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;